import { useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { formatMinorAmount } from "@/types/shopping.ts";
import {
  shoppingRecommendationRows,
  type ShoppingRecommendationProps,
} from "@/shared/shoppingPresentation.ts";

export function ShoppingRecommendations({
  recommendations,
  products,
  canBuy,
  pending,
  onPrepare,
  onDetails,
}: ShoppingRecommendationProps) {
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const rows = shoppingRecommendationRows(recommendations, products);
  return (
    <section aria-label="Recommendations" className="flex flex-col gap-4">
      {rows.some((row) => row.changed) ? (
        <p role="status" className="text-sm">
          Product details changed since I compared these. Ask me to reassess before choosing.
        </p>
      ) : (
        <p className="text-sm leading-relaxed">{recommendations.summary}</p>
      )}
      {rows.map((row) => (
        <article key={row.number} className="flex flex-col gap-3 rounded-lg border p-4">
          <p className="text-xs font-semibold text-primary">{row.label}</p>
          <div className="flex items-start gap-3">
            {row.image && !failedImages.includes(row.image.url) ? (
              <img
                src={row.image.url}
                alt={row.image.alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="h-24 w-24 shrink-0 rounded-md object-contain"
                onError={() =>
                  setFailedImages((previous) =>
                    row.image ? [...previous, row.image.url] : previous,
                  )
                }
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold">
                {row.product?.title ?? "Product unavailable"}
              </h3>
              {row.variant ? (
                <>
                  <p className="text-sm">
                    {formatMinorAmount(
                      row.variant.amountMinor * row.selection.quantity,
                      row.variant.currency,
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">{row.variant.merchant}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.variant.options.map((option) => option.label).join(" · ")}
                    {row.variant.options.length ? " · " : ""}Quantity {row.selection.quantity}
                  </p>
                </>
              ) : null}
            </div>
          </div>
          {row.changed ? (
            <p className="text-sm">This option needs another look before checkout.</p>
          ) : (
            <>
              <p className="text-sm leading-relaxed">{row.reason}</p>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Tradeoff: </span>
                {row.tradeoff}
              </p>
            </>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canBuy && !row.changed && row.variant?.checkoutMode !== "merchant" ? (
              <Button
                size="sm"
                disabled={pending}
                data-statsig-name="shopping-use-recommendation"
                aria-label={`Prepare option ${row.number}: ${row.product?.title}`}
                onClick={() => onPrepare(row.number)}
              >
                {pending ? "Preparing…" : "Go with this"}
              </Button>
            ) : null}
            {canBuy && row.variant?.checkoutMode === "merchant" && !row.changed ? (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-statsig-name="shopping-recommendation-merchant"
              >
                <a href={row.variant.url} target="_blank" rel="noopener noreferrer">
                  Continue on merchant website ↗
                </a>
              </Button>
            ) : null}
            {row.product ? (
              <Button
                size="sm"
                variant="ghost"
                data-statsig-name="shopping-recommendation-details"
                aria-label={`View details for ${row.product.title}`}
                onClick={() => onDetails(row.selection)}
              >
                Details
              </Button>
            ) : null}
            {row.variant ? (
              <a
                href={row.variant.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs underline"
                aria-label={`View ${row.product?.title} on ${row.variant.merchant}`}
              >
                Product page ↗
              </a>
            ) : null}
          </div>
          {row.variant?.checkoutMode === "merchant" ? (
            <p className="text-xs text-muted-foreground">
              I can recommend this item, but checkout needs to be completed on the merchant’s
              website.
            </p>
          ) : null}
        </article>
      ))}
      <p className="text-xs text-muted-foreground">
        Shipping and tax are confirmed at checkout. I’ll ask for approval before paying.
      </p>
    </section>
  );
}
